import { BettingStructure, LeaderboardType } from '@repo/database/src/types';

import { StakeName } from './stake-types';

export type StakeMeasurement = 'bigBet' | 'bigBlind';

export interface StakeRangeDefinition {
  min: number;
  max: number | null;
  minInclusive: boolean;
  maxInclusive: boolean;
  lowerDisplay?: string;
  upperDisplay?: string;
}

export const STAKE_TIER_ORDER: StakeName[] = [StakeName.MICRO, StakeName.LOW, StakeName.MEDIUM, StakeName.HIGH];

export const STAKE_RANGES: Record<StakeMeasurement, Record<StakeName, StakeRangeDefinition>> = {
  bigBet: {
    [StakeName.MICRO]: {
      min: 0,
      max: 4,
      minInclusive: true,
      maxInclusive: true,
      upperDisplay: '2/4',
    },
    [StakeName.LOW]: {
      min: 4,
      max: 16,
      minInclusive: false,
      maxInclusive: true,
      lowerDisplay: '2/4',
      upperDisplay: '8/16',
    },
    [StakeName.MEDIUM]: {
      min: 16,
      max: 200,
      minInclusive: false,
      maxInclusive: false,
      lowerDisplay: '8/16',
      upperDisplay: '100/200',
    },
    [StakeName.HIGH]: {
      min: 200,
      max: null,
      minInclusive: true,
      maxInclusive: true,
      lowerDisplay: '100/200',
    },
  },
  bigBlind: {
    [StakeName.MICRO]: {
      min: 0,
      max: 0.5,
      minInclusive: true,
      maxInclusive: true,
      upperDisplay: '0.25/0.50',
    },
    [StakeName.LOW]: {
      min: 0.5,
      max: 4,
      minInclusive: false,
      maxInclusive: true,
      lowerDisplay: '0.25/0.50',
      upperDisplay: '2/4',
    },
    [StakeName.MEDIUM]: {
      min: 4,
      max: 50,
      minInclusive: false,
      maxInclusive: false,
      lowerDisplay: '2/4',
      upperDisplay: '25/50',
    },
    [StakeName.HIGH]: {
      min: 50,
      max: null,
      minInclusive: true,
      maxInclusive: true,
      lowerDisplay: '25/50',
    },
  },
};

export interface StakeFilterGroup {
  label: StakeName;
  stakeNames: StakeName[];
}

export const DEFAULT_STAKE_FILTER_GROUPS: StakeFilterGroup[] = [
  {
    label: StakeName.MICRO,
    stakeNames: [StakeName.MICRO],
  },
  {
    label: StakeName.LOW,
    stakeNames: [StakeName.LOW],
  },
  {
    label: StakeName.MEDIUM,
    stakeNames: [StakeName.MEDIUM],
  },
  {
    label: StakeName.HIGH,
    stakeNames: [StakeName.HIGH],
  },
];

export const LEADERBOARD_STAKE_GROUPS: Record<LeaderboardType, StakeName[]> = {
  [LeaderboardType.MICRO]: [StakeName.MICRO, StakeName.LOW],
  [LeaderboardType.MEDIUM]: [StakeName.MEDIUM],
  [LeaderboardType.HIGH]: [StakeName.HIGH],
};

export const BETTING_STRUCTURE_MEASUREMENTS: Record<BettingStructure, StakeMeasurement> = {
  [BettingStructure.NO_LIMIT]: 'bigBlind',
  [BettingStructure.POT_LIMIT]: 'bigBlind',
  [BettingStructure.FIXED_LIMIT]: 'bigBet',
  [BettingStructure.MIXED_LIMIT]: 'bigBet',
};

const resolveOrderedStakeNames = (stakeNames: StakeName[]): StakeName[] => {
  return STAKE_TIER_ORDER.filter((stakeName) => stakeNames.includes(stakeName));
};

export const combineStakeRanges = (measurement: StakeMeasurement, stakeNames: StakeName[]): StakeRangeDefinition => {
  const orderedNames = resolveOrderedStakeNames(stakeNames);

  if (!orderedNames.length) {
    throw new Error(`combineStakeRanges called without stake names for measurement ${measurement}`);
  }

  const first = STAKE_RANGES[measurement][orderedNames[0]];
  const last = STAKE_RANGES[measurement][orderedNames[orderedNames.length - 1]];

  return {
    min: first.min,
    max: last.max,
    minInclusive: first.minInclusive,
    maxInclusive: last.maxInclusive,
    lowerDisplay: first.lowerDisplay,
    upperDisplay: last.upperDisplay,
  };
};

export const isValueWithinRange = (value: number | null | undefined, range: StakeRangeDefinition): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  const meetsLowerBound = range.minInclusive ? value >= range.min : value > range.min;
  if (!meetsLowerBound) {
    return false;
  }

  if (range.max === null || range.max === undefined) {
    return true;
  }

  return range.maxInclusive ? value <= range.max : value < range.max;
};

export const resolveStakeNameForValue = (
  measurement: StakeMeasurement,
  value: number | null | undefined,
): StakeName | null => {
  if (value === null || value === undefined) {
    return null;
  }

  for (const stakeName of STAKE_TIER_ORDER) {
    const range = STAKE_RANGES[measurement][stakeName];
    if (isValueWithinRange(value, range)) {
      return stakeName;
    }
  }

  return null;
};
